<template>
  <PortfolioFormLayout :loading="loading">
    <template #header>
      <h5 v-if="isOffering" class="my-2">
        {{ isUpdateForm ? 'Update' : 'Create' }} Product Offering
      </h5>
      <h5 v-else class="my-2">{{ isUpdateForm ? 'Update' : 'Create' }} Product</h5>
    </template>
    <template #body>
      <OfferingForm
        v-if="isOffering"
        :pid="pid"
        :product="modelProduct"
        :offering="modelOffering"
        :is-update-form="isUpdateForm"
        @update:offering="updateOffering"
        @create:offering="createOffering"
      />
      <ProductForm
        v-else
        :pid="pid"
        :product="modelProduct"
        :is-update-form="isUpdateForm"
        @update:products="emit('update:products')"
      />
    </template>
    <template #footer>
      <OfferingAddOns v-if="isOffering && isUpdateForm" :pid="pid" />
    </template>
    <template #aside>
      <OfferingListGroup
        v-if="isOffering === false && !isCreateForm && modelProduct"
        :loading="loading"
        :product="modelProduct"
        @delete:offering="deleteOffering"
      />
    </template>
  </PortfolioFormLayout>
</template>
<script lang="ts" setup>
const emit = defineEmits<{
  'update:products': [];
}>();

const route = useRoute();

// stores
const productsStore = useProductsStore();
const componentTypesStore = useComponentTypesStore();
const costPoolsStore = useCostPoolsStore();
const featuresStore = useFeaturesStore();
const laborComponentsStore = useLaborComponentsStore();
const productComponentsStore = useProductComponentsStore();
const serviceLevelObjectivesStore = useServiceLevelObjectivesStore();

const getProducts = computed(() => productsStore.getProducts);

const loading = ref(true);
const pid = computed(() => Number(route.params.id));
const isUpdateForm = computed(() => route.path.includes('/update/'));
const isCreateForm = computed(() => route.path.includes('/create'));
const isOffering = computed(() => (route.query?.po ? true : false));

// The local refs to update
const product = ref<Product>();
const offering = ref<ProductOffering>();

/**
 * On mount, the first page of products is fetched, however, the pid in the route
 * can belong to pages other than the first page of products.
 * We're using writable computed here to try look in the store first, otherwise,
 * the setup functions below will make requests to find the product/offering by
 * the route pid
 */
const modelProduct = computed({
  get: () => getProducts.value.find((p) => p.id === pid.value) || product.value,
  set: (value) => (product.value = value),
});

const modelOffering = computed(() => {
  // Then find that same offering in the found product
  return modelProduct.value?.offerings?.find((o) => o.id === pid.value) || offering.value;
});

// The functions to mutate the local refs
const createOffering = (o: ProductOffering) =>
  product.value !== undefined && product.value.offerings.push(o);
const updateOffering = (o: ProductOffering) => {
  if (product.value !== undefined) {
    // Find & update the target object.
    const index = product.value.offerings.findIndex(({id}) => id === o.id);
    if (index >= 0) Object.assign(product.value.offerings[index], o);
  }
  offering.value = o;
};
const deleteOffering = (id: number) => {
  if (product.value !== undefined) {
    // Find & update the target object.
    const index = product.value.offerings.findIndex((o) => o.id === id);
    if (index >= 0) product.value.offerings.splice(index, 1);
  }
  offering.value = undefined;
};

/**
 * Gets the product for the update product {@link update/:pid} or create
 * offering {@link create/:pid?po=1} routes using the route pid
 */
const setupProductUpdateOrOfferingCreate = async () => {
  // pid is the product id
  try {
    if (modelProduct.value === undefined) {
      const p = await productsStore.readProductById(pid.value);
      product.value = p;
    }
  } catch (errorMessage) {
    console.log(errorMessage);
  }
};

/**
 * Gets the offering for the update offering {@link update/:pid?po=1} route
 * using the route pid & gets the product using the offering's product_id
 */
const setupOfferingUpdate = async () => {
  // pid is the offering id
  try {
    // If the models fail, fetch the offering using pid, then fetch the product using product_id
    if (modelProduct.value === undefined || modelOffering.value === undefined) {
      const o = await productsStore.readProductOfferingById(pid.value);
      offering.value = o;
      if (offering.value !== undefined) {
        const p = await productsStore.readProductById(offering.value.product_id);
        product.value = p;
      }
    }
  } catch (errorMessage) {
    console.log(errorMessage);
  }
};

/**
 * #TODO: RND-1317
 * This is not great, the Product forms require the ability to search through
 * all items, ideally this might be condensed into a one or a number of search endpoints.
 * Here, we temprarily fetch all pages.
 */
const fetchData = async () => {
  try {
    await Promise.all([
      // Product & Internal Components
      featuresStore.readFeatures({limit: 500}),
      productComponentsStore.readProductComponents({limit: 500}),
      serviceLevelObjectivesStore.readServiceLevelObjectives({limit: 500}),
      laborComponentsStore.readLaborComponents({limit: 500}),
      // Dependant forms data
      componentTypesStore.readComponentTypes({limit: 500}),
      costPoolsStore.readCostPools({limit: 500}),
    ]);
  } catch (errorMessage) {
    console.log(errorMessage);
  }
};

const setupFormType = async () => {
  try {
    if (isOffering.value) {
      if (isUpdateForm.value) await setupOfferingUpdate();
      else await setupProductUpdateOrOfferingCreate();
      await fetchData();
    } else {
      if (isUpdateForm.value) {
        await setupProductUpdateOrOfferingCreate();
        await fetchData();
      }
    }
  } catch (errorMessage) {
    console.log(errorMessage);
  } finally {
    loading.value = false;
  }
};

onMounted(() => setupFormType());
</script>
